import { create } from 'zustand';
import { db, storage } from '../config/firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { extractDatesFromSeminarText } from '../utils/seminarArchive';
import {
    SeminarScheduleDay,
    normalizeScheduleDays,
    sortScheduleDays,
    formatScheduleDayLine,
    resolveSeminarDateText,
} from '../utils/seminarSchedule';
import { unpublishPublicOnlineEvent } from '../utils/publicOnlineEventSync';

export function extractStoragePathFromDownloadUrl(url: string): string | null {
    if (!url?.trim()) return null;
    if (!url.startsWith('http')) return url.trim();
    if (!url.includes('firebasestorage.googleapis.com') && !url.includes('firebasestorage.app')) return null;
    const match = url.match(/\/o\/([^?]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export async function deleteStorageFileByDownloadUrl(url: string): Promise<void> {
    if (!url?.trim()) return;
    const storagePath = extractStoragePathFromDownloadUrl(url);
    if (!storagePath) return;
    try {
        await deleteObject(ref(storage, storagePath));
    } catch (e) {
        console.error('[eventStore] Storage-Löschen fehlgeschlagen:', { url, storagePath, error: e });
    }
}

export interface EventModel {
    id: string;
    eventType: string; // 💉 NEU: Art des Events
    title: string;
    subtitle: string;
    titleImage: string; 
    baseDate: string; 
    overrideDate: string; 
    hasDoubleSession: boolean;
    timeMorning: string; 
    linkMorning: string;
    timeEvening: string; 
    linkEvening: string;
    currentTopic: string;
    description: string;
    imageUrl: string;
    /** Alias / Backend-Kompatibilität für Themenbild */
    topicImage?: string;
    targetAudience: string[];
    isRecurring: boolean;
    recurrencePattern: string; 
    isPaidEvent: boolean; 
    registrationUrl: string;
    isArchived?: boolean;
    archivedAt?: string;
    /** ISO-Datum (YYYY-MM-DD), an das der aktuelle Monatsinhalt gebunden ist */
    monthlyContentBoundDate?: string;
    /** Public Online-Landing (/event/:slug) */
    isPublicOnlineEvent?: boolean;
    publicSlug?: string;
    publicTeaser?: string;
    publicBullets?: string[];
    publicSpeakers?: Array<{ name: string; role?: string; bio?: string; imageUrl?: string }>;
    publicCtaLabel?: string;
    publicThankYouText?: string;
    publicFormat?: string;
    /** true = wird aufgezeichnet, false = wird nicht aufgezeichnet, null/undefined = noch nicht gewählt */
    willBeRecorded?: boolean | null;
    /** Monats-Extra überschreibt willBeRecorded, solange gesetzt */
    monthlyWillBeRecorded?: boolean | null;
}

const normalizeDateInput = (value: unknown): unknown | null => {
    if (value == null) return null;

    if (
        typeof value === 'object' &&
        value !== null &&
        'toDate' in value &&
        typeof (value as { toDate: () => Date }).toDate === 'function'
    ) {
        return (value as { toDate: () => Date }).toDate();
    }

    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
    }

    return value;
};

export const getEventActiveDateRaw = (
    event: Pick<EventModel, 'overrideDate' | 'baseDate'>
): unknown | null => normalizeDateInput(event.overrideDate) ?? normalizeDateInput(event.baseDate);

export const parseLocalEventDate = (dateVal?: unknown): Date | null => {
    const normalized = normalizeDateInput(dateVal);
    if (normalized == null) return null;

    if (normalized instanceof Date) {
        return new Date(normalized.getFullYear(), normalized.getMonth(), normalized.getDate());
    }

    const dateStr = String(normalized).trim();
    if (!dateStr) return null;

    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const deMatch = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (deMatch) {
        const [, day, month, year] = deMatch;
        return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const parsed = new Date(dateStr);
    if (isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

export const normalizeEventTargetAudience = (targetAudience: unknown): string[] => {
    const raw = Array.isArray(targetAudience)
        ? targetAudience.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
        : typeof targetAudience === 'string' && targetAudience.trim()
          ? [targetAudience.trim()]
          : [];
    // Kanonisch (Mail-Kreise) + Legacy-Aliase für bestehende Docs
    const out = new Set<string>();
    for (const entry of raw) {
        const k = entry.trim().toLowerCase();
        if (k === 'mandanten' || k === 'basic' || k === 'clients' || k === 'client') {
            out.add('mandanten');
            out.add('basic'); // Legacy-Sichtbarkeit EventDashboard
        } else if (k === 'intern' || k === 'interne' || k === 'insider' || k === 'vip') {
            out.add('intern');
            out.add('insider');
        } else if (k === 'team') {
            out.add('team');
            out.add('basic');
            out.add('insider');
        } else if (k === 'extern' || k === 'externe' || k === 'external' || k === 'leads') {
            out.add('extern');
            out.add('external');
        }
    }
    return Array.from(out);
};

/** Themenbild: imageUrl (kanonisch) + ältere Alias-Felder */
export const resolveEventTopicImage = (ev: Partial<EventModel> & Record<string, unknown>): string => {
    const candidates = [ev.imageUrl, ev.topicImage, ev.extraImage, ev.monatsBild];
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) return c.trim();
    }
    return '';
};

/** Monatsthema-Text inkl. Alias-Felder */
export const resolveEventTopicText = (ev: Partial<EventModel> & Record<string, unknown>): string => {
    const candidates = [ev.currentTopic, ev.monatsthema, ev.topic, ev.theme];
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) return c.trim();
    }
    return '';
};

/** Firestore-Dokument → kanonische Felder für UI/Mail */
export const normalizeEventRecord = (raw: Record<string, unknown>, id: string): EventModel => {
    const base = raw as unknown as EventModel;
    return {
        ...base,
        id,
        currentTopic: resolveEventTopicText(raw),
        imageUrl: resolveEventTopicImage(raw),
        titleImage: typeof raw.titleImage === 'string' ? raw.titleImage : (base.titleImage || ''),
        description: typeof raw.description === 'string' ? raw.description : (base.description || ''),
    };
};

/** Bezahlte Seminare mit manuellem Promo-Flag (Collection `events`) */
export interface PromotionSeminar {
    id: string;
    title: string;
    date: string;
    scheduleDays?: SeminarScheduleDay[];
    location?: string;
    marketingTeaser?: string;
    marketingImage?: string;
    showPromotionPopup?: boolean;
    promoStartDate?: string;
    isOnline?: boolean;
    isArchived?: boolean;
}

export type PopupStateEntry = {
    lastDismissedAt?: number;
    permanentlyDisabled?: boolean;
};

export type PopupStatesMap = Record<string, PopupStateEntry>;

export type PopupCandidate = {
    kind: 'reminder' | 'promotion';
    id: string;
    title: string;
    subtitle?: string;
    description?: string;
    imageUrl?: string;
    /** ISO ms Start der Morgensession (nur reminder) — für Live-Countdown */
    startsAtMs?: number;
    dateLabel?: string;
    timeLabel?: string;
    ctaLabel: string;
    /** Externer Link (Zoom) oder interner Pfad (/seminar/…) */
    ctaHref: string;
    ctaIsExternal: boolean;
    /** Optional: In-App Seitenkey statt Hard-Navigation */
    navigatePageKey?: string;
};

export const POPUP_STATES_STORAGE_KEY = 'peone_popup_states';
export const POPUP_SESSION_SHOWN_KEY = 'peone_popup_session_shown';
export const POPUP_SESSION_DEFER_PROMO_KEY = 'peone_popup_defer_promo';
const POPUP_SNOOZE_MS = 24 * 60 * 60 * 1000;
/** Live-Event-Popup: Fenster vor der Morgensession (wie Mail: Double-Session nur Vormittag) */
const REMINDER_WINDOW_MS = 24 * 60 * 60 * 1000;
const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

/** Seminar-Start (frühester Tag) liegt in [heute … heute+14 Tage]. */
export const isSeminarWithinPromoWindow = (
    dateText?: string,
    now = new Date(),
    scheduleDays?: SeminarScheduleDay[] | null
): boolean => {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const fromSchedule = sortScheduleDays(normalizeScheduleDays(scheduleDays || []))
        .map((d) => {
            const parsed = new Date(`${d.date}T12:00:00`);
            return isNaN(parsed.getTime()) ? null : parsed;
        })
        .filter((d): d is Date => !!d);
    let dates = fromSchedule.length ? fromSchedule : extractDatesFromSeminarText(dateText);
    if (dates.length === 0) {
        const fallback = parseLocalEventDate(dateText);
        if (fallback) dates = [fallback];
    }
    if (dates.length === 0) return false;

    const start = dates.reduce((earliest, d) =>
        d.getTime() < earliest.getTime() ? d : earliest
    );
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const diff = startDay.getTime() - today.getTime();
    return diff >= 0 && diff <= FOURTEEN_DAYS_MS;
};

export const loadPopupStates = (): PopupStatesMap => {
    try {
        const raw = localStorage.getItem(POPUP_STATES_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

export const savePopupStates = (states: PopupStatesMap): void => {
    try {
        localStorage.setItem(POPUP_STATES_STORAGE_KEY, JSON.stringify(states));
    } catch (e) {
        console.error('[eventStore] peone_popup_states speichern fehlgeschlagen:', e);
    }
};

export const isPopupEligible = (eventId: string, states: PopupStatesMap = loadPopupStates(), now = Date.now()): boolean => {
    const entry = states[eventId];
    if (!entry) return true;
    if (entry.permanentlyDisabled) return false;
    if (typeof entry.lastDismissedAt === 'number' && now - entry.lastDismissedAt < POPUP_SNOOZE_MS) {
        return false;
    }
    return true;
};

export const markPopupSnoozed = (eventId: string): void => {
    const states = loadPopupStates();
    states[eventId] = { ...states[eventId], lastDismissedAt: Date.now() };
    savePopupStates(states);
};

export const markPopupPermanentlyDisabled = (eventId: string): void => {
    const states = loadPopupStates();
    states[eventId] = {
        ...states[eventId],
        permanentlyDisabled: true,
        lastDismissedAt: Date.now(),
    };
    savePopupStates(states);
};

export const hasSessionPopupBeenShown = (): boolean => {
    try {
        return sessionStorage.getItem(POPUP_SESSION_SHOWN_KEY) === '1';
    } catch {
        return false;
    }
};

export const markSessionPopupShown = (): void => {
    try {
        sessionStorage.setItem(POPUP_SESSION_SHOWN_KEY, '1');
    } catch {
        /* ignore */
    }
};

export const markSessionDeferPromo = (): void => {
    try {
        sessionStorage.setItem(POPUP_SESSION_DEFER_PROMO_KEY, '1');
    } catch {
        /* ignore */
    }
};

export const isSessionPromoDeferred = (): boolean => {
    try {
        return sessionStorage.getItem(POPUP_SESSION_DEFER_PROMO_KEY) === '1';
    } catch {
        return false;
    }
};

export const parseTimeToHoursMinutes = (timeStr?: string): { h: number; m: number } | null => {
    const s = String(timeStr || '').trim();
    if (!s) return null;
    const match = s.match(/(\d{1,2})(?::(\d{2}))?/);
    if (!match) return null;
    const h = Number(match[1]);
    const m = Number(match[2] || 0);
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return { h, m };
};

export const getEventSessionStart = (event: EventModel, useEvening = false): Date | null => {
    const day = parseLocalEventDate(getEventActiveDateRaw(event));
    if (!day) return null;
    const timeStr = useEvening && event.hasDoubleSession ? event.timeEvening : event.timeMorning;
    const hm = parseTimeToHoursMinutes(timeStr) || { h: 10, m: 0 };
    return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hm.h, hm.m, 0, 0);
};

/**
 * Anker für Erinnerung: immer die Morgensession.
 * Double-Session (z. B. Education Monday): wie Mail — nur Vormittag triggert, nicht Abend.
 */
export const getReminderAnchorStart = (event: EventModel): Date | null =>
    getEventSessionStart(event, false);

/** @deprecated Alias — nutzt nur noch die Morgensession / 24h-Fenster */
export const getUpcomingSessionStarts = (event: EventModel, now = new Date()): Date[] => {
    const morning = getReminderAnchorStart(event);
    if (morning && morning.getTime() > now.getTime()) return [morning];
    return [];
};

/** true, wenn die Morgensession in (0, 24h] liegt */
export const isEventWithinReminderWindow = (event: EventModel, now = new Date()): boolean => {
    const start = getReminderAnchorStart(event);
    if (!start) return false;
    const diff = start.getTime() - now.getTime();
    return diff > 0 && diff <= REMINDER_WINDOW_MS;
};

/** @deprecated Alias für isEventWithinReminderWindow */
export const isEventWithinNext12Hours = isEventWithinReminderWindow;

export type LiveCountdownKind = 'upcoming' | 'live' | 'ended';

export type LiveCountdownInfo = {
    kind: LiveCountdownKind;
    /** Anzeige z. B. "● LIVE IN 2 STD." */
    label: string;
};

/** Meet-Beitritt ab 15 Min. vor Session-Start */
export const MEET_JOIN_EARLY_MS = 15 * 60 * 1000;
/** Meet/Live-Fenster endet 2h nach Start */
export const MEET_LIVE_AFTER_MS = 2 * 60 * 60 * 1000;

/** true, wenn Meet-Link freigeschaltet ist (15 Min. vor Start bis 2h danach). */
export const canJoinEventMeet = (start: Date | null, now = new Date()): boolean => {
    if (!start || Number.isNaN(start.getTime())) return false;
    const diffMs = start.getTime() - now.getTime();
    return diffMs <= MEET_JOIN_EARLY_MS && diffMs > -MEET_LIVE_AFTER_MS;
};

/**
 * Live-Countdown aus lokalem Session-Start (Browser-Zeit, kein UTC-Shift).
 * Minutengenau wie PEBase — kein Aufrunden ganzer Stunden.
 */
export const formatLiveCountdown = (start: Date | null, now = new Date()): LiveCountdownInfo | null => {
    if (!start || Number.isNaN(start.getTime())) return null;
    const diffMs = start.getTime() - now.getTime();

    // Bis 2h nach Start = LIVE
    if (diffMs <= 0) {
        if (diffMs > -MEET_LIVE_AFTER_MS) {
            return { kind: 'live', label: '● JETZT LIVE' };
        }
        return { kind: 'ended', label: 'BEENDET' };
    }

    const totalMinutes = Math.max(1, Math.floor(diffMs / 60_000));

    if (totalMinutes < 60) {
        return { kind: 'upcoming', label: `● LIVE IN ${totalMinutes} MIN.` };
    }

    if (totalMinutes < 24 * 60) {
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        if (mins === 0) {
            return { kind: 'upcoming', label: `● LIVE IN ${hours} STD.` };
        }
        return { kind: 'upcoming', label: `● LIVE IN ${hours} STD. ${mins} MIN.` };
    }

    const days = Math.floor(totalMinutes / (24 * 60));
    const remHours = Math.floor((totalMinutes % (24 * 60)) / 60);
    if (remHours === 0) {
        return {
            kind: 'upcoming',
            label: `● LIVE IN ${days} TAG${days === 1 ? '' : 'EN'}.`,
        };
    }
    return {
        kind: 'upcoming',
        label: `● LIVE IN ${days} TAG${days === 1 ? '' : 'EN'} ${remHours} STD.`,
    };
};

const buildReminderCandidate = (event: EventModel): PopupCandidate => {
    // Popup führt immer zur In-App Event-Seite — nie direkt zum Zoom-/Extern-Link
    const isPaid = event.isPaidEvent === true;
    const start = getReminderAnchorStart(event);
    const day = parseLocalEventDate(getEventActiveDateRaw(event));

    return {
        kind: 'reminder',
        id: event.id,
        title: event.title || 'Live-Event',
        subtitle: event.subtitle || event.currentTopic || undefined,
        description: event.description || undefined,
        imageUrl: event.titleImage || event.imageUrl || undefined,
        startsAtMs: start ? start.getTime() : undefined,
        dateLabel: day
            ? day.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : undefined,
        timeLabel: event.timeMorning ? `${event.timeMorning} Uhr` : undefined,
        ctaLabel: isPaid ? 'Jetzt anmelden' : 'Zum Event',
        ctaHref: '/events',
        ctaIsExternal: false,
        navigatePageKey: 'events',
    };
};

const buildPromotionCandidate = (seminar: PromotionSeminar): PopupCandidate => {
    const days = sortScheduleDays(normalizeScheduleDays(seminar.scheduleDays));
    const dateLabel = days.length
        ? days.map((d) => formatScheduleDayLine(d)).join(' · ')
        : resolveSeminarDateText(seminar) || undefined;
    return {
        kind: 'promotion',
        id: seminar.id,
        title: seminar.title || 'Seminar',
        subtitle: seminar.location || undefined,
        description: seminar.marketingTeaser || undefined,
        imageUrl: seminar.marketingImage || undefined,
        dateLabel,
        ctaLabel: 'Jetzt anmelden',
        ctaHref: `/seminar/${seminar.id}`,
        ctaIsExternal: false,
    };
};

/**
 * Prio 1: 24h-Live-Erinnerung vor Morgensession (system_events)
 * Prio 2: Seminar-Promotion (events mit showPromotionPopup)
 * Max. 1 Kandidat; Session-/Anti-Spam-Filter inklusive.
 */
export const resolvePopupCandidate = (
    liveEvents: EventModel[],
    promotionSeminars: PromotionSeminar[],
    now = new Date()
): PopupCandidate | null => {
    if (hasSessionPopupBeenShown()) return null;

    const states = loadPopupStates();
    const inReminderWindow = liveEvents.filter((ev) => isEventWithinReminderWindow(ev, now));

    // Aktives 24h-Fenster (nicht permanent disabled) → Promo diese Session zurückstellen
    const activeReminderForDefer = inReminderWindow.filter((ev) => !states[ev.id]?.permanentlyDisabled);
    if (activeReminderForDefer.length > 0) {
        markSessionDeferPromo();
    }

    const reminderEligible = inReminderWindow
        .filter((ev) => isPopupEligible(ev.id, states, now.getTime()))
        .sort((a, b) => {
            const aStart = getReminderAnchorStart(a)?.getTime() ?? Infinity;
            const bStart = getReminderAnchorStart(b)?.getTime() ?? Infinity;
            return aStart - bStart;
        });

    if (reminderEligible.length > 0) {
        return buildReminderCandidate(reminderEligible[0]);
    }

    if (isSessionPromoDeferred() || activeReminderForDefer.length > 0) {
        return null;
    }

    const promoEligible = promotionSeminars
        .filter((s) => s.showPromotionPopup === true && !s.isArchived)
        .filter((s) => isPopupEligible(s.id, states, now.getTime()))
        .filter((s) => isSeminarWithinPromoWindow(s.date, now, s.scheduleDays))
        .filter((s) => {
            if (!s.promoStartDate) return true;
            const start = parseLocalEventDate(s.promoStartDate);
            if (!start) return true;
            const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            return dayStart.getTime() <= today.getTime();
        });

    if (promoEligible.length === 0) return null;
    return buildPromotionCandidate(promoEligible[0]);
};

interface EventStore {
    events: EventModel[];
    archivedEvents: EventModel[];
    promotionSeminars: PromotionSeminar[];
    isLoading: boolean;
    error: string | null;
    fetchEvents: () => Promise<void>;
    fetchPromotionSeminars: () => Promise<void>;
    addEvent: (event: Omit<EventModel, 'id'>) => Promise<string | void>;
    updateEvent: (id: string, event: Partial<EventModel>) => Promise<void>;
    deleteEvent: (id: string, titleImage?: string, imageUrl?: string) => Promise<void>;
    /** Soft-Archiv: Event bleibt erhalten, öffentliche Anmeldung wird gestoppt */
    archiveEvent: (id: string, publicSlug?: string) => Promise<void>;
    restoreEventFromArchive: (id: string) => Promise<void>;
    /** Thema + Themenbild zurücksetzen (inkl. Storage), Datum bleibt */
    clearMonthlyContent: (id: string, imageUrl?: string) => Promise<void>;
}

const pad2 = (n: number) => n.toString().padStart(2, '0');

export const toEventDateISO = (d: Date): string =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Monatsinhalt-Felder leeren (Thema, Text, Themenbild-Aliase, Monats-Aufzeichnung) */
export const EMPTY_MONTHLY_CONTENT_FIELDS: Record<string, string | null> = {
    currentTopic: '',
    description: '',
    imageUrl: '',
    topicImage: '',
    monatsthema: '',
    topic: '',
    theme: '',
    monatsBild: '',
    extraImage: '',
    monthlyContentBoundDate: '',
    monthlyWillBeRecorded: null,
};

/** Oben = Serie, unten = Monat. Der Monatsbeitrag gewinnt, sobald er gesetzt ist. */
export const resolveEventRecording = (
    ev: Pick<EventModel, 'willBeRecorded' | 'monthlyWillBeRecorded'>
): boolean | null => {
    if (typeof ev.monthlyWillBeRecorded === 'boolean') return ev.monthlyWillBeRecorded;
    if (typeof ev.willBeRecorded === 'boolean') return ev.willBeRecorded;
    return null;
};

export const eventHasMonthlyContent = (ev: Partial<EventModel> & Record<string, unknown>): boolean =>
    !!(resolveEventTopicText(ev) || resolveEventTopicImage(ev) || String(ev.description || '').trim());

/** Tage bis Event-Tag (0 = heute, negativ = vorbei). null wenn kein Datum. */
export const getDaysUntilEventDate = (event: EventModel, now = new Date()): number | null => {
    const day = parseLocalEventDate(getEventActiveDateRaw(event));
    if (!day) return null;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((day.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};

/**
 * 10-Tage-Erinnerung: nächstes Event in 0..10 Tagen und kein Monats-Thema hinterlegt.
 * Nur für wiederkehrende / Serien-Events — einmalige Events: strikt unterdrückt.
 */
export const shouldShowMissingTopicReminder = (event: EventModel, now = new Date()): boolean => {
    if (event.isRecurring !== true) return false;
    if (event.isArchived === true) return false;
    const days = getDaysUntilEventDate(event, now);
    if (days == null || days < 0 || days > 10) return false;
    return !resolveEventTopicText(event as Partial<EventModel> & Record<string, unknown>);
};

export const calculateNextOccurrence = (pattern: string, currentBaseDate: string): string => {
    const parsed = parseLocalEventDate(currentBaseDate) ?? new Date(currentBaseDate);
    let year = parsed.getFullYear();
    let month = parsed.getMonth() + 1;

    if (month > 11) {
        month = 0;
        year += 1;
    }

    if (pattern === 'fixed' || !pattern) {
        const nextDate = new Date(year, month, parsed.getDate());
        return toEventDateISO(nextDate);
    }

    const getNthDayOfMonth = (y: number, m: number, nth: number, targetDayOfWeek: number) => {
        const firstDay = new Date(y, m, 1);
        let dayOffset = targetDayOfWeek - firstDay.getDay();
        if (dayOffset < 0) dayOffset += 7;
        return new Date(y, m, 1 + dayOffset + (nth - 1) * 7);
    };

    let nextDate: Date;
    switch (pattern) {
        case 'first-monday': nextDate = getNthDayOfMonth(year, month, 1, 1); break;
        case 'second-monday': nextDate = getNthDayOfMonth(year, month, 2, 1); break;
        case 'first-wednesday': nextDate = getNthDayOfMonth(year, month, 1, 3); break;
        default:
            nextDate = new Date(year, month, parsed.getDate());
    }

    return toEventDateISO(nextDate);
};

const useEventStore = create<EventStore>((set, get) => ({
    events: [],
    archivedEvents: [],
    promotionSeminars: [],
    isLoading: false,
    error: null,

    fetchPromotionSeminars: async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'events'));
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            const list: PromotionSeminar[] = [];

            querySnapshot.docs.forEach((document) => {
                const data = document.data() as Record<string, unknown>;
                if (data.showPromotionPopup !== true) return;
                if (data.isArchived === true) return;

                const dateRaw = data.date;
                const dateStr = typeof dateRaw === 'string' ? dateRaw : String(dateRaw ?? '');
                const scheduleDays = normalizeScheduleDays(data.scheduleDays);
                if (!isSeminarWithinPromoWindow(dateStr, now, scheduleDays)) return;

                list.push({
                    id: document.id,
                    title: typeof data.title === 'string' ? data.title : '',
                    date: dateStr,
                    scheduleDays,
                    location: typeof data.location === 'string' ? data.location : undefined,
                    marketingTeaser:
                        typeof data.marketingTeaser === 'string' ? data.marketingTeaser : undefined,
                    marketingImage:
                        typeof data.marketingImage === 'string' ? data.marketingImage : undefined,
                    showPromotionPopup: true,
                    promoStartDate:
                        typeof data.promoStartDate === 'string' ? data.promoStartDate : undefined,
                    isOnline: data.isOnline === true,
                    isArchived: false,
                });
            });

            set({ promotionSeminars: list });
        } catch (error: any) {
            console.error('[eventStore] fetchPromotionSeminars:', error);
            set({ promotionSeminars: [] });
        }
    },

    fetchEvents: async () => {
        set({ isLoading: true, error: null });
        try {
            const querySnapshot = await getDocs(collection(db, 'system_events'));
            const activeEvents: EventModel[] = [];
            const archivedEvents: EventModel[] = [];
            const now = new Date();
            now.setHours(0, 0, 0, 0); 

            for (const document of querySnapshot.docs) {
                const data = normalizeEventRecord(document.data() as Record<string, unknown>, document.id);
                const isRecurring = data.isRecurring === true;

                // Bereits archiviert
                if (data.isArchived === true) {
                    archivedEvents.push({ ...data, isRecurring });
                    continue;
                }

                const activeDateRaw = getEventActiveDateRaw(data);
                if (activeDateRaw == null) continue;

                const activeDate = parseLocalEventDate(activeDateRaw);
                if (!activeDate) continue;

                // Einmal-Event abgelaufen → Archiv (kein Löschen)
                if (!isRecurring && activeDate < now) {
                    const archivedAt = new Date().toISOString();
                    updateDoc(doc(db, 'system_events', document.id), {
                        isArchived: true,
                        archivedAt,
                    }).catch((err) => console.error('Auto-Archiv system_events:', err));
                    archivedEvents.push({
                        ...data,
                        isRecurring,
                        isArchived: true,
                        archivedAt,
                    });
                    continue;
                }

                // Recurring abgelaufen: Datum auf nächsten Termin schieben UND Monatsinhalt leeren.
                // Verhindert, dass Alt-Thema/Bild am Folgetermin (z. B. 02.09.) hängen bleiben.
                if (isRecurring && activeDate < now) {
                    let rolloverBase =
                        String(normalizeDateInput(data.baseDate) ?? activeDateRaw);
                    let newBaseDate = rolloverBase;
                    let guard = 0;
                    let rolledDate = activeDate;

                    while (rolledDate && rolledDate < now && guard < 24) {
                        newBaseDate = calculateNextOccurrence(
                            data.recurrencePattern,
                            rolloverBase
                        );
                        rolloverBase = newBaseDate;
                        rolledDate = parseLocalEventDate(newBaseDate);
                        guard += 1;
                    }

                    const topicImageToDelete = resolveEventTopicImage(
                        data as Partial<EventModel> & Record<string, unknown>
                    );
                    if (topicImageToDelete) {
                        deleteStorageFileByDownloadUrl(topicImageToDelete).catch((err) =>
                            console.error('Event-Rollover Storage:', err)
                        );
                    }

                    data.baseDate = newBaseDate;
                    data.overrideDate = '';
                    Object.assign(data, EMPTY_MONTHLY_CONTENT_FIELDS);

                    updateDoc(doc(db, 'system_events', document.id), {
                        baseDate: newBaseDate,
                        overrideDate: '',
                        ...EMPTY_MONTHLY_CONTENT_FIELDS,
                    }).catch((err) => console.error('Event-Rollover Reset:', err));
                } else if (isRecurring && eventHasMonthlyContent(data as Partial<EventModel> & Record<string, unknown>)) {
                    // Altlast-Guard: Inhalt ohne/mit falscher Datumsbindung gehört nicht zum aktuellen Termin
                    const activeIso = toEventDateISO(activeDate);
                    const bound = String(
                        (data as EventModel & { monthlyContentBoundDate?: string }).monthlyContentBoundDate || ''
                    ).trim();
                    if (!bound || bound !== activeIso) {
                        const topicImageToDelete = resolveEventTopicImage(
                            data as Partial<EventModel> & Record<string, unknown>
                        );
                        if (topicImageToDelete) {
                            deleteStorageFileByDownloadUrl(topicImageToDelete).catch((err) =>
                                console.error('Event-Altlast Storage:', err)
                            );
                        }
                        Object.assign(data, EMPTY_MONTHLY_CONTENT_FIELDS);
                        updateDoc(doc(db, 'system_events', document.id), {
                            ...EMPTY_MONTHLY_CONTENT_FIELDS,
                        }).catch((err) => console.error('Event-Altlast Clear:', err));
                    }
                }

                activeEvents.push({ ...data, isRecurring });
            }

            const byDateAsc = (a: EventModel, b: EventModel) => {
                const dateA = parseLocalEventDate(getEventActiveDateRaw(a))?.getTime() ?? 0;
                const dateB = parseLocalEventDate(getEventActiveDateRaw(b))?.getTime() ?? 0;
                return dateA - dateB;
            };
            activeEvents.sort(byDateAsc);
            archivedEvents.sort((a, b) => byDateAsc(b, a));

            set({ events: activeEvents, archivedEvents, isLoading: false });
        } catch (error: any) {
            set({ error: error.message || 'Fehler beim Laden.', isLoading: false });
        }
    },

    addEvent: async (eventData) => {
        set({ isLoading: true, error: null });
        try {
            const newDocRef = doc(collection(db, 'system_events'));
            const newEvent = { ...eventData, id: newDocRef.id, isArchived: false };
            await setDoc(newDocRef, newEvent);
            await get().fetchEvents();
            return newDocRef.id;
        } catch (error: any) {
            set({ error: error.message || 'Fehler beim Speichern.', isLoading: false });
        }
    },

    updateEvent: async (id, eventData) => {
        set({ isLoading: true, error: null });
        try {
            await updateDoc(doc(db, 'system_events', id), eventData);
            await get().fetchEvents();
        } catch (error: any) {
            set({ error: error.message || 'Fehler beim Update.', isLoading: false });
        }
    },

    deleteEvent: async (id, titleImage, imageUrl) => {
        set({ isLoading: true, error: null });
        try {
            if (titleImage) {
                await deleteStorageFileByDownloadUrl(titleImage);
            }
            if (imageUrl) {
                await deleteStorageFileByDownloadUrl(imageUrl);
            }
            await deleteDoc(doc(db, 'system_events', id));
            await get().fetchEvents();
        } catch (error: any) {
            set({ error: error.message || 'Fehler beim Löschen.', isLoading: false });
        }
    },

    archiveEvent: async (id, publicSlug) => {
        set({ isLoading: true, error: null });
        try {
            await updateDoc(doc(db, 'system_events', id), {
                isArchived: true,
                archivedAt: new Date().toISOString(),
                isPublicOnlineEvent: false,
            });
            const slug = String(publicSlug || '').trim();
            if (slug) {
                await unpublishPublicOnlineEvent(slug);
            }
            await get().fetchEvents();
        } catch (error: any) {
            set({ error: error.message || 'Fehler beim Archivieren.', isLoading: false });
        }
    },

    restoreEventFromArchive: async (id) => {
        set({ isLoading: true, error: null });
        try {
            await updateDoc(doc(db, 'system_events', id), {
                isArchived: false,
                archivedAt: '',
            });
            await get().fetchEvents();
        } catch (error: any) {
            set({ error: error.message || 'Fehler beim Wiederherstellen.', isLoading: false });
        }
    },

    clearMonthlyContent: async (id, imageUrl) => {
        set({ isLoading: true, error: null });
        try {
            if (imageUrl) {
                await deleteStorageFileByDownloadUrl(imageUrl);
            }
            await updateDoc(doc(db, 'system_events', id), {
                ...EMPTY_MONTHLY_CONTENT_FIELDS,
            });
            await get().fetchEvents();
        } catch (error: any) {
            set({ error: error.message || 'Fehler beim Zurücksetzen.', isLoading: false });
        }
    },
}));

export default useEventStore;